import { useMemo } from 'react';
import {
    RadarChart, Radar, PolarGrid,
    PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer,
    Tooltip,
} from 'recharts';
import { useCachedAssessmentTemplates } from '../../hooks/useCachedData';
import { ASSESSMENT_SHORT_LABELS } from '../../data/categoryDefinitions';
import './FocusRadarChart.css';

const STALE_DAYS = 30;

function isStale(completedAt) {
    if (!completedAt) return true;
    return Date.now() - new Date(completedAt).getTime() > STALE_DAYS * 24 * 60 * 60 * 1000;
}

function formatDate(iso) {
    if (!iso) return null;
    return new Date(iso).toLocaleDateString();
}

const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
        <div className="frc-tooltip">
            <span className="frc-tooltip-name">{d.fullName}</span>
            <span className="frc-tooltip-val">{Math.round(d.rawHealth)}% health</span>
            {d.date && <span className="frc-tooltip-date">{d.date}</span>}
            {d.stale && <span className="frc-tooltip-stale">Stale — retake recommended</span>}
            {d.fromPrevBin && <span className="frc-tooltip-prev">From previous period</span>}
        </div>
    );
};

export default function FocusRadarChart({ focusData, summary, prevSummary, color }) {
    const { assessmentTemplates } = useCachedAssessmentTemplates();

    const { radarData, hasStalePoints } = useMemo(() => {
        if (!assessmentTemplates) return { radarData: [], hasStalePoints: false };

        const currentAssessments = summary?.assessments || {};
        const prevAssessments = prevSummary?.assessments || {};
        const raw = [];
        let anyStale = false;

        for (const aid of focusData.assessment_ids) {
            const currentEntry = currentAssessments[aid];
            const prevEntry = prevAssessments[aid];

            const entry = currentEntry || prevEntry;
            if (!entry || entry.total_pct == null) continue;

            const fromPrevBin = !currentEntry && !!prevEntry;
            const stale = fromPrevBin || isStale(entry.completed_at);
            if (stale) anyStale = true;

            const tmpl = assessmentTemplates[aid];
            const label = tmpl?.display_name || aid;
            const shortName = ASSESSMENT_SHORT_LABELS[aid] || (label.length > 16 ? label.slice(0, 14) + '...' : label);

            raw.push({
                name: shortName,
                fullName: label,
                rawHealth: Math.max(0, Math.min(100, 100 - entry.total_pct)),
                date: formatDate(entry.completed_at),
                stale,
                fromPrevBin,
            });
        }

        if (raw.length === 0) return { radarData: [], hasStalePoints: false };

        // Absolute scaling: the radius is the actual health % (100 - total_pct), so a
        // genuinely healthy assessment plots near the edge. (Previously min-max
        // normalized within the card, which pinned the lowest assessment to the center
        // regardless of its absolute score, making good profiles look low/spiky.)
        const data = raw.map(p => ({
            ...p,
            health: p.rawHealth,
            freshHealth: p.stale ? null : p.rawHealth,
            staleHealth: p.stale ? p.rawHealth : null,
        }));

        return { radarData: data, hasStalePoints: anyStale };
    }, [focusData, summary, prevSummary, assessmentTemplates]);

    if (radarData.length < 3) {
        return (
            <div className="frc-placeholder">
                <span className="frc-placeholder-text">
                    Complete at least 3 assessments to see your radar chart
                </span>
                <span className="frc-placeholder-count">
                    {radarData.length}/3 done
                </span>
            </div>
        );
    }

    return (
        <ResponsiveContainer width="100%" height={200}>
            <RadarChart data={radarData} outerRadius="75%">
                <PolarGrid stroke="#e5e7eb" />
                <PolarAngleAxis
                    dataKey="name"
                    tick={{ fontSize: 10, fill: '#6b7280' }}
                />
                <PolarRadiusAxis
                    domain={[0, 100]}
                    tick={false}
                    axisLine={false}
                />
                {hasStalePoints && (
                    <Radar
                        dataKey="staleHealth"
                        stroke="#d1d5db"
                        fill="#d1d5db"
                        fillOpacity={0.05}
                        strokeWidth={1.5}
                        strokeDasharray="4 3"
                        dot={{ r: 3, fill: '#d1d5db', stroke: '#d1d5db' }}
                        animationDuration={600}
                        animationEasing="ease-out"
                        connectNulls={false}
                    />
                )}
                <Radar
                    dataKey="freshHealth"
                    stroke={color}
                    fill={color}
                    fillOpacity={0.15}
                    strokeWidth={2}
                    dot={{ r: 3, fill: color }}
                    animationDuration={800}
                    animationEasing="ease-out"
                    connectNulls={false}
                />
                <Tooltip content={<CustomTooltip />} />
            </RadarChart>
        </ResponsiveContainer>
    );
}
